import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dictionary } from "lodash";
import { isEmpty, map, uniq } from "lodash/fp";
import { useLocation } from "react-router";
import { shallow } from "zustand/shallow";

import { getPulseClips } from "@app/analytics/Pulse/api";
import type {
  ClipsListQueryReturn,
  ForYouPageKind,
} from "@app/analytics/Pulse/types";
import apiClient from "@app/common/api/apiClient";
import { useAuthenticated } from "@app/common/auth/useAuth";
import { toast } from "@app/common/hooks/useToasts";
import type {
  ApiActionItemClip,
  ApiActionItemUpdateClip,
  ApiGptHighlight,
  ApiKeyQuestionAnswerClip,
} from "@app/common/types";

import useForYou from "./useForYou";
import useForYourTeams from "./useForYourTeams";
import useForYourWorkspace from "./useForYourWorkspace";
import useForYouTags from "./useForYouTags";
import usePulseStore from "../stores/pulse";

const EMPTY_ANSWERS: Dictionary<ApiKeyQuestionAnswerClip> = {};
const EMPTY_STATUSES: Dictionary<ApiActionItemUpdateClip> = {};
const EMPTY_CLIPS: Dictionary<ApiGptHighlight> = {};

const getTemporaryId = () => `temp-${Math.random() * Math.random()}`;

const usePulseClips = (pageFromParams?: ForYouPageKind) => {
  const queryClient = useQueryClient();
  const { user } = useAuthenticated();
  const location = useLocation();
  const page =
    pageFromParams ?? (location.pathname.split("/").pop() as ForYouPageKind);

  const { forYouQuery } = useForYou();
  const { tagResultQuery } = useForYouTags();
  const { workspaceQuery } = useForYourWorkspace();
  const { teamResultQuery } = useForYourTeams();

  const {
    forYouRange,
    forYourWorkspaceRange,
    forYouTagsRange,
    forYourTeamsRange,
  } = usePulseStore(
    (state) => ({
      forYouRange: state.forYou.selectedRange,
      forYourWorkspaceRange: state.forYourWorkspace.selectedRange,
      forYouTagsRange: state.forYouTags.selectedRange,
      forYourTeamsRange: state.forYourTeams.selectedRange,
    }),
    shallow
  );
  const result =
    page === "for-you"
      ? forYouQuery.data
      : page === "tags"
        ? tagResultQuery.data
        : page === "teams"
          ? teamResultQuery.data
          : workspaceQuery.data;

  const rangeForPage =
    page === "for-you"
      ? forYouRange
      : page === "tags"
        ? forYouTagsRange
        : page === "teams"
          ? forYourTeamsRange
          : forYourWorkspaceRange;

  const idsForSelectedPulse = [
    ...uniq(map("id", result?.actionItems)),
    ...uniq(map("id", result?.keyQuestions)),
  ];

  const listClipsQueryKey = [
    "pulse",
    "clips",
    user.id,
    page,
    rangeForPage.value,
    ...idsForSelectedPulse,
  ];

  const clipsListQuery = useQuery({
    queryKey: listClipsQueryKey,
    queryFn: () => getPulseClips(idsForSelectedPulse),
    enabled: !isEmpty(idsForSelectedPulse),
  });

  const createAnswerMutation = useMutation({
    mutationFn: apiClient.clips.createClip,
    onMutate: async (body) => {
      if (!body.parentClipId) return;

      await queryClient.cancelQueries({ queryKey: listClipsQueryKey });
      const data = queryClient.getQueryData(
        listClipsQueryKey
      ) as ClipsListQueryReturn;

      const updatedData = { ...data };

      const optimisticAnswer: ApiKeyQuestionAnswerClip = {
        id: getTemporaryId(),
        clipType: "key_question_answer",
        startTime: body.createTime,
        endTime: body.createTime,
        sessionId: body.sessionId,
        text: body.text,
        generatedByRead: false,
        status: "user_accept",
      };

      updatedData.answers[body.parentClipId] = optimisticAnswer;

      queryClient.setQueryData(listClipsQueryKey, updatedData);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: listClipsQueryKey }),
    onError: () => {
      toast.error(
        "Something went wrong and your answer could not be saved. Please refresh the page or try again later."
      );
    },
  });

  const updateClipMutation = useMutation({
    mutationFn: apiClient.clips.updateClip,
    onMutate: async (body) => {
      if (body.type === "key_question_answer" && body.status) {
        // don't do an optimistic update on an answer status update; the patch request is quick and
        // there are sufficient loading states, so the optimistic update is more awkward than without it
        return;
      }

      await queryClient.cancelQueries({ queryKey: listClipsQueryKey });
      const data = queryClient.getQueryData(
        listClipsQueryKey
      ) as ClipsListQueryReturn;

      const updatedData = { ...data };

      if (body.type === "key_question_answer" && body.parentClipId) {
        const currentAnswer = updatedData.answers[body.parentClipId];
        if (currentAnswer) {
          updatedData.answers[body.parentClipId] = {
            ...currentAnswer,
            ...body,
          } as ApiKeyQuestionAnswerClip;
        }
      }

      if (body.type === "action_item") {
        const currentActionItem = updatedData.actionItems[body.id];

        if (currentActionItem) {
          updatedData.actionItems[body.id] = {
            ...currentActionItem,
            status: body.status,
          } as ApiActionItemClip;
        }
      }

      queryClient.setQueryData(listClipsQueryKey, updatedData);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: listClipsQueryKey }),
  });

  return {
    isInitialLoading: clipsListQuery.isLoading,
    actionItems: clipsListQuery.data?.actionItems ?? EMPTY_CLIPS,
    answers: clipsListQuery.data?.answers ?? EMPTY_ANSWERS,
    questions: clipsListQuery.data?.questions ?? EMPTY_CLIPS,
    statuses: clipsListQuery.data?.statuses ?? EMPTY_STATUSES,
    createAnswer: createAnswerMutation,
    updateClip: updateClipMutation,
  };
};

export default usePulseClips;
